// Packages
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Watch,
} from "@stencil/core";

let tooltipIdCounter = 0;

interface ITooltipPosition {
  x: number;
  y: number;
  placement: "top" | "bottom" | "left" | "right";
}

interface IComputeTooltipPositionOptions {
  triggerRect: DOMRect;
  tooltipRect: DOMRect;
  position: "top" | "bottom" | "left" | "right";
  gap: number;
  viewportWidth: number;
  viewportHeight: number;
}

/**
 * `fx-tooltip` is a headless, accessible tooltip. It exposes every styleable
 * surface via `part` attributes and its state through `data-*` and ARIA
 * attributes so themes can customize it without internal styles.
 *
 * The tooltip shows after a configurable delay when the slotted trigger is
 * hovered or focused, and hides when the pointer leaves or the trigger loses
 * focus. The `visible` prop allows manual control.
 *
 * @slot - The trigger element that receives hover/focus events.
 *
 * @part trigger - The wrapper around the slotted trigger content.
 * @part tooltip - The floating tooltip bubble.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-tooltip",
  shadow: true,
})
export class FxTooltip {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  private tooltipId = `fx-tooltip-${++tooltipIdCounter}`;

  private triggerRef?: HTMLDivElement;

  private tooltipRef?: HTMLDivElement;

  /** Handle for the position measurement frame. */
  private positionFrame?: number;

  /** Handle for the delayed show timer. */
  private showTimeout?: ReturnType<typeof setTimeout>;

  /** Tracks whether viewport listeners are currently attached. */
  private viewportListenersAdded = false;

  /** Whether the pointer is currently over the component. */
  private isHovering = false;

  /** Whether focus is currently inside the component. */
  private isFocusing = false;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - The text displayed inside the tooltip bubble.
   */
  @Prop()
  text: string = "";

  /**
   * @prop - The preferred position of the tooltip relative to the trigger.
   * The actual placement is flipped and clamped to stay inside the viewport.
   */
  @Prop()
  position: "top" | "bottom" | "left" | "right" = "top";

  /**
   * @prop - The delay in milliseconds before the tooltip appears on
   * hover/focus. Hiding always happens immediately.
   */
  @Prop()
  delay: number = 0;

  /**
   * @prop - When `true`, the tooltip animates in/out. Reflects to
   * `data-animation` and the `fx-tooltip-animated` host class.
   */
  @Prop()
  animation: boolean = true;

  /**
   * @prop - When `true`, the tooltip is visible. Allows manual control; the
   * component also toggles this prop on hover/focus.
   */
  @Prop({ mutable: true })
  visible: boolean = false;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when focus enters the component.
   */
  @Event()
  fxFocus!: EventEmitter<FocusEvent>;

  /**
   * @event - Emitted when focus leaves the component.
   */
  @Event()
  fxBlur!: EventEmitter<FocusEvent>;

  // --------------------------------------------
  // State
  // --------------------------------------------

  /**
   * The resolved placement after viewport clamping/flip. Reflected to
   * `data-position`.
   */
  @State()
  private placement: "top" | "bottom" | "left" | "right" = "top";

  /**
   * The viewport coordinates of the tooltip bubble.
   */
  @State()
  private coords = { x: 0, y: 0 };

  /**
   * Whether the tooltip coordinates have been measured. The bubble is kept
   * `visibility: hidden` until this is true to avoid a flash at the origin.
   */
  @State()
  private isPositioned = false;

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  componentWillLoad() {
    this.placement = this.position;
    if (this.visible) {
      this.open();
    }
  }

  componentDidRender() {
    if (this.visible && !this.isPositioned) {
      this.schedulePosition();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  // --------------------------------------------
  // Watchers
  // --------------------------------------------

  @Watch("visible")
  visibleChanged(newValue: boolean) {
    if (newValue) {
      this.open();
    } else {
      this.close();
    }
  }

  @Watch("position")
  positionChanged() {
    this.placement = this.position;
    if (this.visible) {
      this.cancelPosition();
      this.isPositioned = false;
    }
  }

  @Watch("text")
  textChanged() {
    if (this.visible) {
      this.cancelPosition();
      this.isPositioned = false;
    }
  }

  // --------------------------------------------
  // Handlers
  // --------------------------------------------

  private handleMouseEnter = (event: MouseEvent) => {
    if (this.isInsideComponent(event.relatedTarget as Node | null)) {
      return;
    }
    if (this.isHovering) {
      return;
    }
    this.isHovering = true;
    this.showWithDelay();
  };

  private handleMouseLeave = (event: MouseEvent) => {
    if (this.isInsideComponent(event.relatedTarget as Node | null)) {
      return;
    }
    this.isHovering = false;
    this.hideIfIdle();
  };

  private handleFocusIn = (event: FocusEvent) => {
    if (this.isInsideComponent(event.relatedTarget as Node | null)) {
      return;
    }
    if (this.isFocusing) {
      return;
    }
    this.isFocusing = true;
    this.fxFocus.emit(event);
    this.showWithDelay();
  };

  private handleFocusOut = (event: FocusEvent) => {
    if (this.isInsideComponent(event.relatedTarget as Node | null)) {
      return;
    }
    this.isFocusing = false;
    this.fxBlur.emit(event);
    this.hideIfIdle();
  };

  // --------------------------------------------
  // Private helpers
  // --------------------------------------------

  private isInsideComponent(node: Node | null | undefined): boolean {
    if (!node) {
      return false;
    }
    if (this.el.contains(node)) {
      return true;
    }
    return this.el.shadowRoot ? this.el.shadowRoot.contains(node) : false;
  }

  private showWithDelay() {
    if (this.visible) {
      return;
    }
    this.clearShowTimeout();
    const delay = Math.max(0, this.delay);
    if (delay > 0) {
      this.showTimeout = setTimeout(() => {
        this.showTimeout = undefined;
        this.visible = true;
      }, delay);
    } else {
      this.visible = true;
    }
  }

  private hide() {
    this.clearShowTimeout();
    if (this.visible) {
      this.visible = false;
    }
  }

  private hideIfIdle() {
    if (!this.isHovering && !this.isFocusing) {
      this.hide();
    }
  }

  private clearShowTimeout() {
    if (this.showTimeout !== undefined) {
      clearTimeout(this.showTimeout);
      this.showTimeout = undefined;
    }
  }

  private cleanup() {
    this.clearShowTimeout();
    this.cancelPosition();
    this.removeViewportListeners();
  }

  private open() {
    this.isPositioned = false;
    this.coords = { x: 0, y: 0 };
    this.addViewportListeners();
  }

  private close() {
    this.clearShowTimeout();
    this.cancelPosition();
    this.isPositioned = false;
    this.coords = { x: 0, y: 0 };
    this.placement = this.position;
    this.removeViewportListeners();
  }

  private addViewportListeners() {
    if (this.viewportListenersAdded) {
      return;
    }
    window.addEventListener("resize", this.handleViewportChange);
    window.addEventListener("scroll", this.handleViewportChange, true);
    this.viewportListenersAdded = true;
  }

  private removeViewportListeners() {
    if (!this.viewportListenersAdded) {
      return;
    }
    window.removeEventListener("resize", this.handleViewportChange);
    window.removeEventListener("scroll", this.handleViewportChange, true);
    this.viewportListenersAdded = false;
  }

  private handleViewportChange = () => {
    if (this.visible) {
      this.schedulePosition();
    }
  };

  private schedulePosition() {
    if (this.positionFrame !== undefined) {
      return;
    }
    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = undefined;
      this.positionTooltip();
    });
  }

  private cancelPosition() {
    if (this.positionFrame !== undefined) {
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = undefined;
    }
  }

  private positionTooltip() {
    if (!this.visible || !this.triggerRef || !this.tooltipRef) {
      return;
    }

    const triggerRect = this.triggerRef.getBoundingClientRect();
    const tooltipRect = this.tooltipRef.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const { x, y, placement } = this.computeTooltipPosition({
      triggerRect,
      tooltipRect,
      position: this.position,
      gap: 8,
      viewportWidth,
      viewportHeight,
    });

    this.coords = { x, y };
    this.placement = placement;
    this.isPositioned = true;
  }

  private computeTooltipPosition(
    options: IComputeTooltipPositionOptions
  ): ITooltipPosition {
    const { triggerRect, tooltipRect, position, gap, viewportWidth, viewportHeight } = options;
    const tw = tooltipRect.width;
    const th = tooltipRect.height;

    const candidates: ITooltipPosition[] = [
      {
        placement: "top",
        x: triggerRect.left + (triggerRect.width - tw) / 2,
        y: triggerRect.top - th - gap,
      },
      {
        placement: "bottom",
        x: triggerRect.left + (triggerRect.width - tw) / 2,
        y: triggerRect.bottom + gap,
      },
      {
        placement: "left",
        x: triggerRect.left - tw - gap,
        y: triggerRect.top + (triggerRect.height - th) / 2,
      },
      {
        placement: "right",
        x: triggerRect.right + gap,
        y: triggerRect.top + (triggerRect.height - th) / 2,
      },
    ];

    let best: (ITooltipPosition & { score: number }) | null = null;

    for (const candidate of candidates) {
      const overlapWidth = Math.max(
        0,
        Math.min(candidate.x + tw, viewportWidth) - Math.max(candidate.x, 0)
      );
      const overlapHeight = Math.max(
        0,
        Math.min(candidate.y + th, viewportHeight) - Math.max(candidate.y, 0)
      );
      const score = overlapWidth * overlapHeight;
      const isPreferred = candidate.placement === position;

      if (
        !best ||
        score > best.score ||
        (score === best.score && isPreferred)
      ) {
        best = { ...candidate, score };
      }
    }

    const clampedX = Math.max(0, Math.min(best!.x, viewportWidth - tw));
    const clampedY = Math.max(0, Math.min(best!.y, viewportHeight - th));

    return { x: clampedX, y: clampedY, placement: best!.placement };
  }

  // --------------------------------------------
  // Render
  // --------------------------------------------

  render() {
    const { visible, animation, placement, coords, isPositioned, text } = this;

    return (
      <Host
        data-visible={visible ? "" : undefined}
        data-position={placement}
        data-animation={animation ? "" : undefined}
        class={{
          "fx-tooltip-visible": visible,
          "fx-tooltip-animated": animation,
        }}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onFocusin={this.handleFocusIn}
        onFocusout={this.handleFocusOut}
      >
        <div part="trigger" ref={(el) => (this.triggerRef = el as HTMLDivElement)}>
          <slot />
        </div>

        <div
          part="tooltip"
          ref={(el) => (this.tooltipRef = el as HTMLDivElement)}
          id={this.tooltipId}
          role="tooltip"
          aria-hidden={visible ? null : "true"}
          hidden={!visible}
          style={{
            left: `${coords.x}px`,
            top: `${coords.y}px`,
            position: "fixed",
            pointerEvents: "none",
            visibility: visible && !isPositioned ? "hidden" : "visible",
          }}
        >
          {text}
        </div>
      </Host>
    );
  }
}
